/**
 * DÍA 3 — Unpredictable Conversation.
 *
 * Rompe el patrón «pregunta conocida → respuesta conocida». ChatGPT debe
 * generar progresivamente cambios de tema, follow-ups, desacuerdo,
 * interrupciones moderadas, misunderstanding, petición de reformulación,
 * información incompleta, repair y turn-taking natural.
 *
 * No convertir la conversación en una entrevista escolar.
 */

import type { Sesion } from '../../tipos.js';

export const D3: Sesion = {
  clave: 'd3',
  tipo: 'dia',
  etiqueta: 'Día 3 · Unpredictable Conversation',
  titulo: 'Día 3 · Conversación impredecible',
  corto: 'Imprev.',
  objetivo:
    'Aprender a reaccionar cuando la conversación cambia y no tenés una respuesta preparada.',
  porQue:
    'Es la forma más directa de romper la automaticidad estrecha que producen las conversaciones laborales repetitivas: las que ya sabés manejar no te enseñan nada nuevo.',
  areasAEvaluar: ['interaccion_turnos', 'habla_espontanea', 'listening'],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd3-imprevisibilidad',
          titulo: 'Imprevisibilidad',
          minutosSugeridos: 17,
          herramienta: 'chatgpt',
          prompt: 'd3',
          objetivo: 'Responder, reparar y reformular en tiempo real.',
          porQue:
            'Los cambios inesperados exigen flexibilidad. Cuando no podés predecir la pregunta, no podés recitar la respuesta.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Aceptá las interrupciones y los cambios de tema sin resistirte.',
            'Si te falta una palabra, rodeala en inglés en vez de pedir la traducción.',
            'Cuando no te entiendan, reformulá: eso es el ejercicio, no un fallo.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y sostené la conversación aunque cambie de tema sin avisar. Sin correcciones durante el primer intento.',
          },
          areas: ['interaccion_turnos', 'habla_espontanea', 'listening'],
        },
        {
          clave: 'd3-bloqueos',
          titulo: 'Qué te bloqueó',
          minutosSugeridos: 3,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 3 minutos.',
          objetivo: 'Identificar el bloqueo real, no el aparente.',
          porQue:
            'Un bloqueo puede ser de vocabulario, de listening, de gramática o simplemente de tiempo de reacción. Son cuatro problemas distintos y se entrenan distinto.',
          instrucciones: [
            'Anotá solo 1 o 2 bloqueos importantes.',
            'Fijate en qué tipo de bloqueo fue: ¿no entendiste, no encontraste la palabra, o no te dio el tiempo?',
            'Guardalos en el Learning Bank con su área si la reconocés.',
          ],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd3-conversacion',
          titulo: 'Conversación naturalista',
          minutosSugeridos: 28,
          herramienta: 'chatgpt',
          prompt: 'd3',
          objetivo: 'Sostener interacción bajo variación constante.',
          porQue:
            'La presión comunicativa específica —tener que entender y reaccionar— es más útil que una dificultad artificial de vocabulario.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Reformulá cuando no te entiendan.',
            'No pidas traducción inmediata: rodealo primero.',
            'Aceptá la velocidad natural aunque se te escape algo.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y mantené una conversación con cambios de tema, desacuerdo y malentendidos. No es una entrevista.',
          },
          areas: ['interaccion_turnos', 'habla_espontanea', 'listening', 'naturalidad'],
        },
        {
          clave: 'd3-repair',
          titulo: 'Repair y transferencia',
          minutosSugeridos: 7,
          herramienta: 'chatgpt',
          prompt: 'd3',
          objetivo: 'Corregir y mover la solución a otra situación.',
          porQue:
            'La situación nueva comprueba que la reparación generalizó, en vez de quedarse pegada al momento en que ocurrió.',
          instrucciones: [
            'Repetí SOLO la parte problemática, no toda la conversación.',
            'Pasá inmediatamente a otro contexto y usá ahí lo mismo.',
          ],
          uso: {
            queHacer:
              'En la misma conversación, rehacé la parte que falló y pedí enseguida un contexto distinto para volver a usarla.',
          },
          areas: ['interaccion_turnos', 'gramatica_fluidez'],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd3-conversacion',
          titulo: 'Conversación naturalista larga',
          minutosSugeridos: 38,
          herramienta: 'chatgpt',
          prompt: 'd3',
          objetivo: 'Simular interacción larga y cambiante.',
          porQue:
            'Más tiempo permite practicar turn-taking, repair, desacuerdo y cambios de tema de verdad. En quince minutos no llega a ponerse difícil.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Mantené una conversación, no una entrevista escolar.',
            'Aceptá la velocidad natural.',
            'Dejá que te interrumpan y practicá volver a entrar al turno.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y sostené una conversación larga con interrupciones, desacuerdo y cambios de tema.',
          },
          areas: ['interaccion_turnos', 'habla_espontanea', 'listening', 'naturalidad'],
        },
        {
          clave: 'd3-repeticion',
          titulo: 'Repetición corregida',
          minutosSugeridos: 7,
          herramienta: 'chatgpt',
          objetivo: 'Consolidar los cambios de mayor impacto.',
          porQue:
            'Una repetición suele bastar. No necesitamos seis: repetir de más convierte el recurso en un guion.',
          instrucciones: [
            'Rehacé la parte clave aplicando el feedback.',
            'Una vez. No seis.',
          ],
          uso: {
            queHacer:
              'En la misma conversación de ChatGPT, rehacé una sola vez la parte que más te costó, aplicando las correcciones.',
          },
          areas: ['gramatica_fluidez', 'naturalidad'],
        },
        {
          clave: 'd3-registro',
          titulo: 'Registro',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Guardar lo que debe reaparecer.',
          porQue: 'El registro de hoy es el material del Día 4.',
          instrucciones: [
            'Guardá como máximo 3 elementos.',
            'Elegí el área de cada uno si la reconocés; si no, dejalo sin clasificar.',
          ],
        },
      ],
    },
  },
};
